import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { medicationInfoService, MedicationInfoFull } from './lib/medicationInfoService';
import { ErrorMessage } from './components/common/ErrorMessage';
import { validationMessages } from './utils/errorMessages';
import { getErrorMessage } from './utils/errorMessages';

interface AddMedicationFormProps {
  onSubmit: () => void;
  onCancel?: () => void;
}

const UNIT_OPTIONS = [
  'tabletka',
  'kapsułka',
  'saszetka',
  'wlewka',
  'czopek',
  'krople',
  'ml',
  'g',
  'mg',
  'wlew dożylny',
  'wstrzyknięcie',
  'sztuka'
];

export const AddMedicationForm: React.FC<AddMedicationFormProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    activeSubstance: '',
    activeSubstanceAmount: '',
    unit: 'tabletka',
    brandName: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!formData.name.trim()) {
      setErrors({ name: validationMessages.required });
      return;
    }

    if (!formData.unit) {
      setErrors({ unit: 'Wybierz jednostkę' });
      return;
    }

    try {
      setLoading(true);
      
      const medicationData: MedicationInfoFull = {
        id: Date.now().toString(),
        name: formData.name.trim(),
        activeSubstance: formData.activeSubstance.trim() || undefined,
        activeSubstanceAmount: formData.activeSubstanceAmount.trim() || undefined,
        unit: formData.unit,
        brandName: formData.brandName.trim() || undefined,
        notes: formData.notes.trim() || undefined
      };

      await medicationInfoService.create(medicationData);
      
      // Reset form
      setFormData({
        name: '',
        activeSubstance: '',
        activeSubstanceAmount: '',
        unit: 'tabletka',
        brandName: '',
        notes: ''
      });

      toast.success('Lek został dodany do listy dostępnych leków!');
      onSubmit();
    } catch (error) {
      console.error('Błąd dodawania leku:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl mx-auto p-6 bg-white rounded-3xl shadow-sm border border-gray-100">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Dodaj nowy lek do listy</h2>
      <p className="text-gray-600 mb-6 text-sm">
        Dodaj lek, który będzie dostępny do wyboru przy tworzeniu planu leczenia.
      </p>
      
      {/* Nazwa leku */}
      <div className="mb-6">
        <label className="block mb-2 text-sm font-semibold text-gray-900">
          Nazwa leku (z dawką) <span className="text-red-500">*</span>:
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => { setFormData({ ...formData, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: '' }); }}
          placeholder="np. Mesalazyna (Pentasa) 500mg"
          className={`w-full px-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all ${errors.name ? 'border-2 border-red-500' : 'border border-gray-200'}`}
        />
        {errors.name && <ErrorMessage message={errors.name} type="inline" />}
        <div className="text-xs text-gray-600 mt-2">
          Pełna nazwa leku z dawką, np. "Mesalazyna (Pentasa) 500mg" lub "Witamina D 2000 IU"
        </div>
      </div>

      {/* Substancja czynna */}
      <div className="mb-6">
        <label className="block mb-2 text-sm font-semibold text-gray-900">
          Substancja czynna:
        </label>
        <input
          type="text"
          value={formData.activeSubstance}
          onChange={(e) => setFormData({ ...formData, activeSubstance: e.target.value })}
          placeholder="np. Mesalazyna"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
        />
      </div>

      {/* Ilość substancji czynnej */}
      <div className="mb-6">
        <label className="block mb-2 text-sm font-semibold text-gray-900">
          Ilość substancji czynnej:
        </label>
        <input
          type="text"
          value={formData.activeSubstanceAmount}
          onChange={(e) => setFormData({ ...formData, activeSubstanceAmount: e.target.value })}
          placeholder="np. 500mg, 2000 IU, 3mg"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
        />
      </div>

      {/* Nazwa handlowa */}
      <div className="mb-6">
        <label className="block mb-2 text-sm font-semibold text-gray-900">
          Nazwa handlowa (marka):
        </label>
        <input
          type="text"
          value={formData.brandName}
          onChange={(e) => setFormData({ ...formData, brandName: e.target.value })}
          placeholder="np. Pentasa, Asacol, Salofalk"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all"
        />
      </div>

      {/* Jednostka */}
      <div className="mb-6">
        <label className="block mb-2 text-sm font-semibold text-gray-900">
          Jednostka <span className="text-red-500">*</span>:
        </label>
        <select
          value={formData.unit}
          onChange={(e) => { setFormData({ ...formData, unit: e.target.value }); if (errors.unit) setErrors({ ...errors, unit: '' }); }}
          className={`w-full px-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all bg-white ${errors.unit ? 'border-2 border-red-500' : 'border border-gray-200'}`}
        >
          {UNIT_OPTIONS.map(unit => (
            <option key={unit} value={unit}>{unit}</option>
          ))}
        </select>
        {errors.unit && <ErrorMessage message={errors.unit} type="inline" />}
      </div>

      {/* Notatki */}
      <div className="mb-6">
        <label className="block mb-2 text-sm font-semibold text-gray-900">
          Notatki (opcjonalnie):
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Dodatkowe informacje o leku..."
          rows={3}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition-all resize-none"
        />
      </div>

      <div className="flex gap-3 mt-6">
        <button
          type="submit"
          disabled={loading}
          className={`flex-1 py-3 px-6 rounded-xl text-white font-semibold text-sm shadow-sm hover:shadow-md transition-all ${
            loading ? 'bg-gray-400 cursor-not-allowed' : ''
          }`}
          style={!loading ? {
            background: 'linear-gradient(to right, #6B7FFF, #8B5CF6)'
          } : {}}
        >
          {loading ? 'Zapisywanie...' : 'Dodaj lek'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold text-sm transition-colors"
          >
            Anuluj
          </button>
        )}
      </div>
    </form>
  );
};
